import React, { useState, useEffect } from "react"
import { useNavigate, Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Breadcrumbs,
  Collapse,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import SaveIcon from "@mui/icons-material/Save"
import InventoryIcon from "@mui/icons-material/Inventory"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import ImageIcon from "@mui/icons-material/Image"
import SettingsIcon from "@mui/icons-material/Settings"
import BuildIcon from "@mui/icons-material/Build"
import StraightenIcon from "@mui/icons-material/Straighten"
import Inventory2Icon from "@mui/icons-material/Inventory2"
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices"
import LayersIcon from "@mui/icons-material/Layers"
import ViewListIcon from "@mui/icons-material/ViewList"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import CloseIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"

import ViewInArIcon from "@mui/icons-material/ViewInAr"

import { postProduct, getVatCategories } from "../../helpers/api_helper"
import ProductImageManager from "./ProductImageManager"
import Pytha3DFileUpload from "./Pytha3DFileUpload"
import { uploadFile, updateFileMetadata } from "../../helpers/fileStorage"
import StructuredCategoryEditor from "components/StructuredCategoryEditor"

// Note: File storage now uses the new FlexFlow Files API
// Files are stored with entity associations for better organization

const MuiProductCreate = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  document.title = t("Create Product") + " | FlexFlow ERP"

  // Form State
  const [name, setName] = useState("")
  const [sku, setSku] = useState("")
  const [description, setDescription] = useState("")
  const [usageType, setUsageType] = useState("MATERIAL")
  const [categories, setCategories] = useState([])
  const [vat, setVat] = useState("")
  const [vatClass, setVatClass] = useState("")
  const [vatCategoryId, setVatCategoryId] = useState("")
  const [vatCategories, setVatCategories] = useState([])
  const [salesPrice, setSalesPrice] = useState("")
  const [b2bSalesPrice, setB2bSalesPrice] = useState("")
  const [unit, setUnit] = useState("Item")

  // Product Dimensions
  const [productDepth, setProductLength] = useState("")
  const [productWidth, setProductWidth] = useState("")
  const [productHeight, setProductHeight] = useState("")
  const [productWeight, setProductWeight] = useState("")

  // Packaging Dimensions
  const [packagingLength, setPackagingLength] = useState("")
  const [packagingWidth, setPackagingWidth] = useState("")
  const [packagingHeight, setPackagingHeight] = useState("")
  const [packagingWeight, setPackagingWeight] = useState("")

  // Variants
  const [variants, setVariants] = useState([])
  const [showVariants, setShowVariants] = useState(false)

  // Pytha 3D State
  const [showPytha3D, setShowPytha3D] = useState(false)
  const [pytha3DFile, setPytha3DFile] = useState(null)

  // UI State
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showDimensions, setShowDimensions] = useState(false)
  const [showRequirements, setShowRequirements] = useState(false)
  const [showServiceTasks, setShowServiceTasks] = useState(false)
  const [productImages, setProductImages] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })

  // Service Tasks State
  const [serviceTasks, setServiceTasks] = useState([])
  const [editingTaskIndex, setEditingTaskIndex] = useState(null)
  const [taskDialogOpen, setTaskDialogOpen] = useState(false)
  const [currentTask, setCurrentTask] = useState({
    name: "",
    description: "",
    duration: "",
    price: "",
    file: null,
    photo: null,
  })

  // Options
  const usageTypeOptions = [
    { value: "MATERIAL", label: t("Material") },
    { value: "PURCHASED-MATERIAL", label: t("Purchased Material") },
    { value: "COMPLETE-PRODUCT", label: t("Complete Product") },
    { value: "SERVICE", label: t("Service") },
    { value: "COMPILED-PRODUCT", label: t("Compiled Product") },
  ]

  const unitOptions = [
    { value: "KG", label: t("Kilograms") },
    { value: "Meter", label: t("Meters") },
    { value: "CM", label: t("Centimeters") },
    { value: "MM", label: t("Millimeters") },
    { value: "Litter", label: t("Liters") },
    { value: "CubicM", label: t("Cubic Meters") },
    { value: "Item", label: t("Items") },
    { value: "Gram", label: t("Grams") },
  ]

  // Fetch VAT categories
  useEffect(() => {
    const fetchVatCategories = async () => {
      try {
        const categories = await getVatCategories()
        setVatCategories(categories.filter(cat => cat.is_active !== false))
        // Set default VAT category if one exists
        const defaultCat = categories.find(cat => cat.is_default && cat.is_active !== false)
        if (defaultCat) {
          setVatCategoryId(defaultCat.id)
          setVat(defaultCat.rate)
        }
      } catch (err) {
        console.error("Failed to fetch VAT categories:", err)
      }
    }
    fetchVatCategories()
  }, [])

  // Service Task Handlers
  const handleOpenTaskDialog = (index = null) => {
    if (index !== null) {
      setCurrentTask({ ...serviceTasks[index] })
      setEditingTaskIndex(index)
    } else {
      setCurrentTask({
        name: "",
        description: "",
        duration: "",
        price: "",
        file: null,
        photo: null,
      })
      setEditingTaskIndex(null)
    }
    setTaskDialogOpen(true)
  }

  const handleCloseTaskDialog = () => {
    setTaskDialogOpen(false)
    setEditingTaskIndex(null)
    setCurrentTask({
      name: "",
      description: "",
      duration: "",
      price: "",
      file: null,
      photo: null,
    })
  }

  const handleSaveTask = () => {
    if (!currentTask.name.trim()) {
      setSnackbar({ open: true, message: t("Task name is required"), severity: "error" })
      return
    }

    if (editingTaskIndex !== null) {
      const updatedTasks = [...serviceTasks]
      updatedTasks[editingTaskIndex] = currentTask
      setServiceTasks(updatedTasks)
    } else {
      setServiceTasks([...serviceTasks, currentTask])
    }
    handleCloseTaskDialog()
  }

  const handleDeleteTask = (index) => {
    setServiceTasks(serviceTasks.filter((_, i) => i !== index))
  }

  const handleTaskFileUpload = async (e, type) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const folder = type === "photo" ? "service-task-photos" : "service-task-files"
      const result = await uploadFile(file, folder)
      setCurrentTask({ ...currentTask, [type]: result.url })
    } catch (error) {
      console.error("Upload error:", error)
      setSnackbar({ open: true, message: t("Failed to upload file"), severity: "error" })
    }
  }

  const calculateServiceTotal = () => {
    return serviceTasks.reduce((sum, task) => sum + (parseFloat(task.price) || 0), 0)
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!name.trim()) {
      setSnackbar({ open: true, message: t("Product name is required"), severity: "error" })
      return
    }
    if (!sku.trim()) {
      setSnackbar({ open: true, message: t("SKU is required"), severity: "error" })
      return
    }

    setIsSaving(true)

    const payload = {
      resource: [
        {
          name: name.trim(),
          description: description.trim(),
          b2bsalesprice: b2bSalesPrice || null,
          usageType: usageType,
          sku: sku.trim(),
          vat: vat || null,
          vat_category_id: vatCategoryId || null,
          stock: 0,
          salesprice: salesPrice || null,
          vatClass: vatClass.trim() || null,
          unit: unit,
          category: categories.length > 0 ? JSON.stringify(categories) : null,
          requirements: JSON.stringify([]),
          productPhotos: JSON.stringify(productImages),
          serviceTasks: JSON.stringify(serviceTasks || []),
          productDepth: productDepth || null,
          productWidth: productWidth || null,
          productHeight: productHeight || null,
          productWeight: productWeight || null,
          packagingLength: packagingLength || null,
          packagingWidth: packagingWidth || null,
          packagingHeight: packagingHeight || null,
          packagingWeight: packagingWeight || null,
          variants: JSON.stringify(variants || []),
        },
      ],
    }

    try {
      const res = await postProduct(payload)
      if (res && res.error) {
        let errorMessage = res.error.message || t("Failed to create product")
        if (res.error.context?.resource?.[0]?.code === "23505") {
          errorMessage = t("A product with this SKU already exists")
        } else if (res.error.context?.resource?.[0]?.message?.includes("duplicate key")) {
          errorMessage = t("A product with this SKU already exists")
        }
        setSnackbar({ open: true, message: errorMessage, severity: "error" })
      } else if (res && res.resource && res.resource[0]) {
        const newProductId = res.resource[0].id
        // Associate Pytha 3D file with the new product
        if (pytha3DFile?.id) {
          await updateFileMetadata(pytha3DFile.id, {
            entity_id: newProductId,
            entity_type: "product-pytha3d",
          })
        }
        setSnackbar({ open: true, message: t("Product created successfully"), severity: "success" })
        navigate("/product-overview/" + newProductId)
      }
    } catch (err) {
      console.error("Error creating product:", err)
      setSnackbar({ open: true, message: t("An unexpected error occurred"), severity: "error" })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Compact Header */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#64748b" }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" sx={{ color: "#94a3b8" }} />}>
              <Link to="/products" style={{ color: "#64748b", textDecoration: "none", fontSize: "0.875rem" }}>
                {t("Products")}
              </Link>
              <Typography color="#1e293b" fontSize="0.875rem" fontWeight={500}>
                {t("Create Product")}
              </Typography>
            </Breadcrumbs>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/products")}
              sx={{
                borderColor: "#e2e8f0",
                color: "#64748b",
                textTransform: "none",
                borderRadius: 1.5,
                "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />}
              onClick={handleSubmit}
              disabled={isSaving}
              sx={{
                bgcolor: "#3b82f6",
                textTransform: "none",
                borderRadius: 1.5,
                "&:hover": { bgcolor: "#2563eb" },
              }}
            >
              {isSaving ? t("Saving...") : t("Create Product")}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={1.5}>
        {/* Left Column - Main Form */}
        <Grid item xs={12} lg={8}>
          {/* Basic Information */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: "#dbeafe", color: "#3b82f6" }}>
                <InventoryIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                  {t("Basic Information")}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  {t("Enter the essential product details")}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("Product Name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder={t("Enter product name")}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("SKU")}
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  required
                  placeholder={t("Enter SKU code")}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={t("Usage Type")}
                  value={usageType}
                  onChange={(e) => setUsageType(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                  {usageTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <StructuredCategoryEditor value={categories} onChange={setCategories} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("Description")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  placeholder={t("Enter product description")}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Pricing */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: "#dcfce7", color: "#16a34a" }}>
                <AttachMoneyIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                  {t("Pricing")}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  {t("Set up product pricing and tax information")}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("Sales Price")}
                  type="number"
                  value={salesPrice}
                  onChange={(e) => setSalesPrice(e.target.value)}
                  placeholder="0.00"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("B2B Price")}
                  type="number"
                  value={b2bSalesPrice}
                  onChange={(e) => setB2bSalesPrice(e.target.value)}
                  placeholder="0.00"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={t("VAT Category")}
                  value={vatCategoryId}
                  onChange={(e) => {
                    const selectedId = e.target.value
                    setVatCategoryId(selectedId)
                    const selectedCat = vatCategories.find(cat => cat.id === selectedId)
                    if (selectedCat) {
                      setVat(selectedCat.rate)
                      setVatClass(selectedCat.code)
                    }
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                  {vatCategories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.label} - {cat.rate}%
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("VAT Rate")}
                  type="number"
                  value={vat}
                  disabled
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText={t("Auto-filled from VAT category")}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Inventory Settings - Collapsible */}
          <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Box
              onClick={() => setShowAdvanced(!showAdvanced)}
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                bgcolor: showAdvanced ? "#f8fafc" : "transparent",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#fef3c7", color: "#d97706" }}>
                  <SettingsIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Inventory Settings")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Unit of measurement and stock settings")}
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" sx={{ color: "#64748b" }}>
                {showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={showAdvanced} unmountOnExit>
              <Box sx={{ p: 2.5, borderTop: "1px solid #e2e8f0" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label={t("Unit of Measurement")}
                      value={unit}
                      onChange={(e) => setUnit(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    >
                      {unitOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Initial Stock")}
                      value="0"
                      disabled
                      helperText={t("Stock is set to 0 for new products")}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Paper>

          {/* Product & Packaging Dimensions - Collapsible */}
          <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Box
              onClick={() => setShowDimensions(!showDimensions)}
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                bgcolor: showDimensions ? "#f8fafc" : "transparent",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#dbeafe", color: "#3b82f6" }}>
                  <StraightenIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Dimensions")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Product and packaging dimensions")}
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" sx={{ color: "#64748b" }}>
                {showDimensions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={showDimensions} unmountOnExit>
              <Box sx={{ p: 2.5, borderTop: "1px solid #e2e8f0" }}>
                {/* Product Dimensions */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <StraightenIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  <Typography variant="subtitle2" fontWeight={600} color="#475569">
                    {t("Product Dimensions")}
                  </Typography>
                </Stack>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Length")}
                      type="number"
                      value={productDepth}
                      onChange={(e) => setProductLength(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Width")}
                      type="number"
                      value={productWidth}
                      onChange={(e) => setProductWidth(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Height")}
                      type="number"
                      value={productHeight}
                      onChange={(e) => setProductHeight(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Weight")}
                      type="number"
                      value={productWeight}
                      onChange={(e) => setProductWeight(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>

                {/* Packaging Dimensions */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Inventory2Icon sx={{ fontSize: 18, color: "#64748b" }} />
                  <Typography variant="subtitle2" fontWeight={600} color="#475569">
                    {t("Packaging Dimensions")}
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Length")}
                      type="number"
                      value={packagingLength}
                      onChange={(e) => setPackagingLength(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Width")}
                      type="number"
                      value={packagingWidth}
                      onChange={(e) => setPackagingWidth(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Height")}
                      type="number"
                      value={packagingHeight}
                      onChange={(e) => setPackagingHeight(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("Weight")}
                      type="number"
                      value={packagingWeight}
                      onChange={(e) => setPackagingWeight(e.target.value)}
                      placeholder="0"
                      InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Paper>

          {/* Dimension Variants - Collapsible */}
          <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Box
              onClick={() => setShowVariants(!showVariants)}
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                bgcolor: showVariants ? "#f8fafc" : "transparent",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#fce7f3", color: "#db2777" }}>
                  <ViewListIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Dimension Variants")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {variants.length > 0
                      ? `${variants.length} ${t("variants")}`
                      : t("Multiple sizes with own SKU and price")}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowVariants(true)
                    setVariants([...variants, { sku: "", depth: "", width: "", height: "", salesprice: "", b2bsalesprice: "" }])
                  }}
                  sx={{
                    bgcolor: "#db2777",
                    textTransform: "none",
                    borderRadius: 1.5,
                    fontSize: "0.75rem",
                    "&:hover": { bgcolor: "#be185d" },
                  }}
                >
                  {t("Add Variant")}
                </Button>
                <IconButton size="small" sx={{ color: "#64748b" }}>
                  {showVariants ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>
            </Box>

            <Collapse in={showVariants} unmountOnExit>
              <Box sx={{ borderTop: "1px solid #e2e8f0" }}>
                {variants.length === 0 ? (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: "#f8fafc",
                      m: 2,
                      borderRadius: 2,
                      border: "1px dashed #e2e8f0",
                    }}
                  >
                    <ViewListIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }} />
                    <Typography variant="body2" color="#64748b">
                      {t("No variants added yet. Click 'Add Variant' to create size options.")}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("SKU")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("Depth")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("Width")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("Height")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("Sales Price")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#475569", py: 1 }}>{t("B2B Price")}</TableCell>
                          <TableCell sx={{ width: 48, py: 1 }} />
                        </TableRow>
                        {variants.map((v, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                value={v.sku}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, sku: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="SKU"
                                sx={{ minWidth: 120, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.depth}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, depth: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="mm"
                                sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.width}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, width: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="mm"
                                sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.height}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, height: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="mm"
                                sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.salesprice}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, salesprice: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="0.00"
                                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ "& p": { fontSize: "0.75rem" } }}>€</InputAdornment> }}
                                sx={{ width: 100, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={v.b2bsalesprice}
                                onChange={(e) => {
                                  const updated = [...variants]
                                  updated[idx] = { ...v, b2bsalesprice: e.target.value }
                                  setVariants(updated)
                                }}
                                placeholder="0.00"
                                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ "& p": { fontSize: "0.75rem" } }}>€</InputAdornment> }}
                                sx={{ width: 100, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                                inputProps={{ style: { fontSize: "0.8rem", padding: "6px 8px" } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <IconButton
                                size="small"
                                onClick={() => setVariants(variants.filter((_, i) => i !== idx))}
                                sx={{ color: "#ef4444" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Paper>

          {/* Pytha 3D File - Collapsible */}
          <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Box
              onClick={() => setShowPytha3D(!showPytha3D)}
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                bgcolor: showPytha3D ? "#f8fafc" : "transparent",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#ede9fe", color: "#8b5cf6" }}>
                  <ViewInArIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Pytha 3D Model")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {pytha3DFile
                      ? pytha3DFile.original_filename || pytha3DFile.name
                      : t("Upload a Pytha 3D file for this product")}
                  </Typography>
                </Box>
              </Stack>
              <IconButton size="small" sx={{ color: "#64748b" }}>
                {showPytha3D ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={showPytha3D} unmountOnExit>
              <Box sx={{ p: 2.5, borderTop: "1px solid #e2e8f0" }}>
                <Pytha3DFileUpload
                  productId={null}
                  existingFile={pytha3DFile}
                  onFileUploaded={(f) => setPytha3DFile(f)}
                  onFileRemoved={() => setPytha3DFile(null)}
                />
              </Box>
            </Collapse>
          </Paper>

          {/* Requirements - Collapsible (hidden for SERVICE type) */}
          {usageType !== "SERVICE" && (
            <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              <Box
                onClick={() => setShowRequirements(!showRequirements)}
                sx={{
                  px: 2.5,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  bgcolor: showRequirements ? "#f8fafc" : "transparent",
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ width: 36, height: 36, bgcolor: "#ede9fe", color: "#8b5cf6" }}>
                    <BuildIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                      {t("Requirements / Materials")}
                    </Typography>
                    <Typography variant="caption" color="#64748b">
                      {t("Add production requirements and bill of materials")}
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small" sx={{ color: "#64748b" }}>
                  {showRequirements ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={showRequirements} unmountOnExit>
                <Box sx={{ p: 2.5, borderTop: "1px solid #e2e8f0" }}>
                  <Box
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: "#f8fafc",
                      borderRadius: 2,
                      border: "1px dashed #e2e8f0",
                    }}
                  >
                    <BuildIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }} />
                    <Typography variant="body2" color="#64748b">
                      {t("Requirements can be added after creating the product")}
                    </Typography>
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          )}

          {/* Service Tasks - Collapsible (only for SERVICE type) */}
          {usageType === "SERVICE" && (
            <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              <Box
                onClick={() => setShowServiceTasks(!showServiceTasks)}
                sx={{
                  px: 2.5,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  bgcolor: showServiceTasks ? "#f8fafc" : "transparent",
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ width: 36, height: 36, bgcolor: "#ede9fe", color: "#8b5cf6" }}>
                    <MiscellaneousServicesIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                      {t("Service Tasks")}
                    </Typography>
                    <Typography variant="caption" color="#64748b">
                      {serviceTasks.length > 0
                        ? `${serviceTasks.length} ${t("tasks")} · €${calculateServiceTotal().toFixed(2)} ${t("total")}`
                        : t("Define tasks to be performed for this service")}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleOpenTaskDialog()
                    }}
                    sx={{
                      bgcolor: "#8b5cf6",
                      textTransform: "none",
                      borderRadius: 1.5,
                      fontSize: "0.75rem",
                      "&:hover": { bgcolor: "#7c3aed" },
                    }}
                  >
                    {t("Add Task")}
                  </Button>
                  <IconButton size="small" sx={{ color: "#64748b" }}>
                    {showServiceTasks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Stack>
              </Box>

              <Collapse in={showServiceTasks} unmountOnExit>
                <Box sx={{ borderTop: "1px solid #e2e8f0" }}>
                  {serviceTasks.length === 0 ? (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: "center",
                        bgcolor: "#f8fafc",
                        m: 2,
                        borderRadius: 2,
                        border: "1px dashed #e2e8f0",
                      }}
                    >
                      <MiscellaneousServicesIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }} />
                      <Typography variant="body2" color="#64748b">
                        {t("No tasks added yet. Click 'Add Task' to create one.")}
                      </Typography>
                    </Box>
                  ) : (
                    <Table size="small">
                      <TableBody>
                        {serviceTasks.map((task, index) => (
                          <TableRow key={index} hover sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                            <TableCell sx={{ py: 1.5 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar sx={{ width: 32, height: 32, bgcolor: "#ede9fe", color: "#8b5cf6" }}>
                                  {index + 1}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight={500} color="#1e293b">
                                    {task.name}
                                  </Typography>
                                  {task.description && (
                                    <Typography variant="caption" color="#64748b" sx={{ display: "block" }}>
                                      {task.description.substring(0, 50)}
                                      {task.description.length > 50 ? "..." : ""}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Stack direction="row" spacing={1}>
                                {task.duration && (
                                  <Chip
                                    icon={<AccessTimeIcon />}
                                    label={`${task.duration} min`}
                                    size="small"
                                    sx={{ bgcolor: "#dbeafe", color: "#1d4ed8", borderRadius: 1 }}
                                  />
                                )}
                                {task.file && (
                                  <Chip
                                    icon={<AttachFileIcon />}
                                    label={t("File")}
                                    size="small"
                                    sx={{ bgcolor: "#f3e8ff", color: "#7c3aed", borderRadius: 1 }}
                                  />
                                )}
                                {task.photo && (
                                  <Chip
                                    icon={<PhotoCameraIcon />}
                                    label={t("Photo")}
                                    size="small"
                                    sx={{ bgcolor: "#fce7f3", color: "#db2777", borderRadius: 1 }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, textAlign: "right" }}>
                              <Typography variant="body2" fontWeight={600} color="#16a34a">
                                €{parseFloat(task.price || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, width: 80 }}>
                              <Stack direction="row" spacing={0.5}>
                                <IconButton size="small" onClick={() => handleOpenTaskDialog(index)} sx={{ color: "#64748b" }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteTask(index)} sx={{ color: "#ef4444" }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          <TableCell colSpan={2} sx={{ py: 1.5 }}>
                            <Typography variant="body2" fontWeight={600} color="#475569">
                              {t("Service Total")}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5, textAlign: "right" }}>
                            <Typography variant="body1" fontWeight={700} color="#8b5cf6">
                              €{calculateServiceTotal().toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  )}
                </Box>
              </Collapse>
            </Paper>
          )}
        </Grid>

        {/* Right Column - Image Upload & Summary */}
        <Grid item xs={12} lg={4}>
          {/* Product Images */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: "#fce7f3", color: "#db2777" }}>
                <ImageIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                {t("Product Images")}
              </Typography>
            </Stack>

            <ProductImageManager
              images={productImages}
              onImagesChange={setProductImages}
            />
          </Paper>

          {/* Quick Summary */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Typography variant="subtitle1" fontWeight={600} color="#1e293b" sx={{ mb: 2 }}>
              {t("Summary")}
            </Typography>

            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="#64748b">{t("Product Name")}</Typography>
                <Typography variant="body2" fontWeight={500} color="#1e293b">
                  {name || "-"}
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: "#f1f5f9" }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="#64748b">{t("SKU")}</Typography>
                <Typography variant="body2" fontWeight={500} color="#1e293b">
                  {sku || "-"}
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: "#f1f5f9" }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="#64748b">{t("Usage Type")}</Typography>
                <Typography variant="body2" fontWeight={500} color="#1e293b">
                  {usageTypeOptions.find(o => o.value === usageType)?.label || "-"}
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: "#f1f5f9" }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="#64748b">{t("Sales Price")}</Typography>
                <Typography variant="body2" fontWeight={500} color="#16a34a">
                  {salesPrice ? `€${salesPrice}` : "-"}
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: "#f1f5f9" }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="#64748b">{t("Unit")}</Typography>
                <Typography variant="body2" fontWeight={500} color="#1e293b">
                  {unitOptions.find(o => o.value === unit)?.label || "-"}
                </Typography>
              </Stack>
            </Stack>

            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={isSaving}
              sx={{
                mt: 3,
                bgcolor: "#3b82f6",
                textTransform: "none",
                borderRadius: 2,
                py: 1.25,
                "&:hover": { bgcolor: "#2563eb" },
              }}
            >
              {isSaving ? t("Creating...") : t("Create Product")}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Service Task Dialog */}
      <Dialog
        open={taskDialogOpen}
        onClose={handleCloseTaskDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              {editingTaskIndex !== null ? t("Edit Task") : t("Add New Task")}
            </Typography>
            <IconButton size="small" onClick={handleCloseTaskDialog}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={t("Task Name")}
              value={currentTask.name}
              onChange={(e) => setCurrentTask({ ...currentTask, name: e.target.value })}
              fullWidth
              required
              placeholder={t("Enter task name...")}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            <TextField
              label={t("Description")}
              value={currentTask.description}
              onChange={(e) => setCurrentTask({ ...currentTask, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder={t("Describe the task...")}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={t("Duration (minutes)")}
                  value={currentTask.duration}
                  onChange={(e) => setCurrentTask({ ...currentTask, duration: e.target.value })}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t("Price")}
                  value={currentTask.price}
                  onChange={(e) => setCurrentTask({ ...currentTask, price: e.target.value })}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: "#94a3b8" }}>€</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Divider />

            <Typography variant="subtitle2" color="#475569" fontWeight={600}>
              {t("Attachments")}
            </Typography>

            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <input
                  type="file"
                  id="task-file-input-create"
                  style={{ display: "none" }}
                  onChange={(e) => handleTaskFileUpload(e, "file")}
                />
                <label htmlFor="task-file-input-create">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<AttachFileIcon />}
                    sx={{
                      borderColor: "#e2e8f0",
                      color: "#64748b",
                      textTransform: "none",
                      borderRadius: 2,
                      py: 1.5,
                      "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
                    }}
                  >
                    {currentTask.file ? t("File Attached") : t("Attach File")}
                  </Button>
                </label>
                {currentTask.file && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                    <Chip
                      label={t("File")}
                      size="small"
                      color="primary"
                      onDelete={() => setCurrentTask({ ...currentTask, file: null })}
                      sx={{ borderRadius: 1 }}
                    />
                  </Stack>
                )}
              </Box>

              <Box sx={{ flex: 1 }}>
                <input
                  type="file"
                  id="task-photo-input-create"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleTaskFileUpload(e, "photo")}
                />
                <label htmlFor="task-photo-input-create">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<PhotoCameraIcon />}
                    sx={{
                      borderColor: "#e2e8f0",
                      color: "#64748b",
                      textTransform: "none",
                      borderRadius: 2,
                      py: 1.5,
                      "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
                    }}
                  >
                    {currentTask.photo ? t("Photo Attached") : t("Attach Photo")}
                  </Button>
                </label>
                {currentTask.photo && (
                  <Box sx={{ mt: 1, borderRadius: 2, overflow: "hidden", border: "1px solid #e2e8f0", position: "relative" }}>
                    <img
                      src={currentTask.photo}
                      alt="Task photo"
                      style={{ width: "100%", height: 80, objectFit: "cover" }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setCurrentTask({ ...currentTask, photo: null })}
                      sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(255,255,255,0.9)" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={handleCloseTaskDialog}
            sx={{ color: "#64748b", textTransform: "none", borderRadius: 2 }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTask}
            startIcon={<SaveIcon />}
            sx={{
              bgcolor: "#3b82f6",
              textTransform: "none",
              borderRadius: 2,
              "&:hover": { bgcolor: "#2563eb" },
            }}
          >
            {editingTaskIndex !== null ? t("Update Task") : t("Add Task")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MuiProductCreate
